import { getToken } from "./utils/authStorage";

const API_BASE = "";

// Build a query string from a params object, dropping null/undefined/"" values.
// (Empty string is a meaningful "(none)" drill-down in a couple of places; those
// callers pass the literal "(none)", so "" here safely means "not set".)
function qs(params = {}) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === "") continue;
    sp.append(k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

async function api(path, options = {}) {
  const token = getToken();
  const headers = { "Content-Type": "application/json", ...options.headers };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  // JSON may fail (proxy returned HTML, body was empty, etc.) — don't
  // bury the real HTTP status behind a generic "Request failed".
  let data = {};
  try { data = await res.json(); } catch { /* non-JSON body */ }
  if (!res.ok) {
    const err = new Error(
      data.error
      || data.message
      || data.reason
      || `${res.status} ${res.statusText || "Request failed"}`.trim()
    );
    // Preserve the structured body so callers can branch on a stable
    // error code (e.g. MEMBER_RESTRICTED) or surface a support reference,
    // not just the human message. Additive — existing callers using
    // err.message keep working.
    err.status = res.status;
    err.code = data.code || null;
    err.data = data;
    throw err;
  }
  return data;
}

export const authApi = {
  adminLogin: (email, password) =>
    api("/api/auth/admin/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  clientLogin: (email, password, venueId, slug) =>
    api("/api/auth/client/login", { method: "POST", body: JSON.stringify({ email, password, venueId, slug }) }),
};

export const adminApi = {
  getVenues: () => api("/api/admin/venues"),
  getVenue: (id) => api(`/api/admin/venues/${id}`),
  createVenue: (data) => api("/api/admin/venues", { method: "POST", body: JSON.stringify(data) }),
  impersonateVenue: (id) => api(`/api/admin/venues/${id}/impersonate`, { method: "POST" }),
  updateVenue: (id, data) => api(`/api/admin/venues/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteVenue: (id) => api(`/api/admin/venues/${id}`, { method: "DELETE" }),
  updateProvider: (id, data) => api(`/api/admin/venues/${id}/provider`, { method: "PUT", body: JSON.stringify(data) }),
  testProvider: (id) => api(`/api/admin/venues/${id}/test-provider`, { method: "POST" }),
  updateStripe: (id, data) => api(`/api/admin/venues/${id}/stripe`, { method: "PUT", body: JSON.stringify(data) }),
  // Comms-prefs CRUD — same shape as the venue-portal helpers but
  // scoped to a venue ID from the URL so admins can edit any venue.
  getVenueMarketingOptions: (id) => api(`/api/admin/venues/${id}/marketing-options`),
  createVenueMarketingOption: (id, data) =>
    api(`/api/admin/venues/${id}/marketing-options`, { method: "POST", body: JSON.stringify(data) }),
  updateVenueMarketingOption: (id, optionId, data) =>
    api(`/api/admin/venues/${id}/marketing-options/${optionId}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteVenueMarketingOption: (id, optionId) =>
    api(`/api/admin/venues/${id}/marketing-options/${optionId}`, { method: "DELETE" }),
  getVenueMarketingReasons: (id) => api(`/api/admin/venues/${id}/marketing-reasons`),
  getClients: (venueId) => api(`/api/admin/venues/${venueId}/clients`),
  createClient: (venueId, data) => api(`/api/admin/venues/${venueId}/clients`, { method: "POST", body: JSON.stringify(data) }),
  getTemplates: (venueId) => api(`/api/admin/venues/${venueId}/templates`),
  updateTemplate: (venueId, key, data) => api(`/api/admin/venues/${venueId}/templates/${key}`, { method: "PUT", body: JSON.stringify(data) }),
  getMembers: (venueId, params) => api(`/api/admin/venues/${venueId}/members?${new URLSearchParams(params)}`),
  getLogs: (venueId, params) => api(`/api/admin/venues/${venueId}/logs?${new URLSearchParams(params)}`),
  createAdmin: (data) => api("/api/admin/users", { method: "POST", body: JSON.stringify(data) }),
  getAdmins: () => api("/api/admin/users"),
  changeAdminPassword: (id, password) => api(`/api/admin/users/${id}/password`, { method: "PUT", body: JSON.stringify({ password }) }),
  changeSelfPassword: (currentPassword, newPassword) => api("/api/admin/change-password", { method: "PUT", body: JSON.stringify({ currentPassword, newPassword }) }),
  changeClientPassword: (venueId, clientId, password) => api(`/api/admin/venues/${venueId}/clients/${clientId}/password`, { method: "PUT", body: JSON.stringify({ password }) }),
  deleteClient: (venueId, clientId) => api(`/api/admin/venues/${venueId}/clients/${clientId}`, { method: "DELETE" }),
  uploadImage: async (file) => {
    const token = getToken();
    const form = new FormData();
    form.append("image", file);
    const res = await fetch("/api/upload", { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Upload failed");
    return data.url;
  },
  getVenueProducts: (venueId) => api(`/api/admin/venues/${venueId}/products`),
  createVenueProduct: (venueId, data) => api(`/api/admin/venues/${venueId}/products`, { method: "POST", body: JSON.stringify(data) }),
  updateVenueProduct: (venueId, pid, data) => api(`/api/admin/venues/${venueId}/products/${pid}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteVenueProduct: (venueId, pid) => api(`/api/admin/venues/${venueId}/products/${pid}`, { method: "DELETE" }),
  getIgtMembershipTypes: (venueId) => api(`/api/admin/venues/${venueId}/igt-membership-types`),
  getSwiftposClassifications: (venueId) => api(`/api/admin/venues/${venueId}/swiftpos-classifications`),
  getCirclescanRefData: (venueId) => api(`/api/admin/venues/${venueId}/circlescan-refdata`),
  createCirclescanRefItem: (venueId, kind, body) =>
    api(`/api/admin/venues/${venueId}/circlescan-refdata/${kind}`, { method: "POST", body: JSON.stringify(body) }),
  updateCirclescanRefItem: (venueId, kind, refId, body) =>
    api(`/api/admin/venues/${venueId}/circlescan-refdata/${kind}/${refId}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteCirclescanRefItem: (venueId, kind, refId) =>
    api(`/api/admin/venues/${venueId}/circlescan-refdata/${kind}/${refId}`, { method: "DELETE" }),
  getSyncStatus: (venueId) => api(`/api/admin/venues/${venueId}/sync-status`),
  syncNow: (venueId) => api(`/api/admin/venues/${venueId}/sync-now`, { method: "POST" }),
  syncPart: (venueId, type) =>
    api(`/api/admin/venues/${venueId}/sync-part`, { method: "POST", body: JSON.stringify({ type }) }),
  importMember: (venueId, { membershipId, cardNumber }) =>
    api(`/api/admin/venues/${venueId}/import-member`, { method: "POST", body: JSON.stringify({ membershipId, cardNumber }) }),
  // ── CSV member import (background job) ─────────────────────
  memberImports: {
    upload: async (venueId, file) => {
      const token = getToken();
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`/api/admin/venues/${venueId}/member-imports`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      let data = {};
      try { data = await res.json(); } catch {}
      if (!res.ok) throw new Error(data.error || `${res.status} ${res.statusText || "Upload failed"}`.trim());
      return data;
    },
    start: (venueId, jobId, mapping, options) =>
      api(`/api/admin/venues/${venueId}/member-imports/${jobId}/start`, {
        method: "POST", body: JSON.stringify({ mapping, options }),
      }),
    status: (venueId, jobId) => api(`/api/admin/venues/${venueId}/member-imports/${jobId}`),
    list:   (venueId) => api(`/api/admin/venues/${venueId}/member-imports`),
    cancel: (venueId, jobId) => api(`/api/admin/venues/${venueId}/member-imports/${jobId}/cancel`, { method: "POST" }),
    downloadErrors: async (venueId, jobId) => {
      const token = getToken();
      const res = await fetch(`/api/admin/venues/${venueId}/member-imports/${jobId}/errors.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No errors file available");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `import-${jobId}-errors.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  },
  getConnectionHealth: (venueId) => api(`/api/admin/venues/${venueId}/connection-health`),
  // Acquisition / campaign analytics (cross-venue; venue_id filters to one).
  getAcquisition: (params) => api(`/api/admin/analytics/acquisition${qs(params)}`),
  getAcquisitionMembers: (params) => api(`/api/admin/analytics/acquisition/members${qs(params)}`),
  acquisitionCsvUrl: (params) => `/api/admin/analytics/acquisition.csv${qs(params)}`,
  cachedMembers: (venueId, params = {}) =>
    api(`/api/admin/venues/${venueId}/cached-members?${new URLSearchParams(params)}`),
  getCronSchedules: () => api("/api/admin/cron-schedules"),
  updateCronSchedule: (id, data) => api(`/api/admin/cron-schedules/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  // Litecard Digital Wallet — READ ONLY template workflow. Template design
  // lives in the Litecard portal. Our app only links a template to a venue
  // and maps source fields to the template's dataFields.
  updateLitecard: (id, data) => api(`/api/admin/venues/${id}/litecard`, { method: "PUT", body: JSON.stringify(data) }),
  updateFoxidConfig: (id, data) => api(`/api/admin/venues/${id}/foxid-config`, { method: "PUT", body: JSON.stringify(data) }),
  // IGT per-venue Cloudflare tunnel
  getIgtTunnel:      (id) => api(`/api/admin/venues/${id}/igt-tunnel`),
  provisionIgtTunnel:(id, data) => api(`/api/admin/venues/${id}/igt-tunnel/provision`, { method: "POST", body: JSON.stringify(data) }),
  deleteIgtTunnel:   (id) => api(`/api/admin/venues/${id}/igt-tunnel`, { method: "DELETE" }),
  // S7000 per-venue Cloudflare tunnel (single hostname → local WebAPI :8444)
  getS7000Tunnel:      (id) => api(`/api/admin/venues/${id}/s7000-tunnel`),
  provisionS7000Tunnel:(id, data) => api(`/api/admin/venues/${id}/s7000-tunnel/provision`, { method: "POST", body: JSON.stringify(data) }),
  deleteS7000Tunnel:   (id) => api(`/api/admin/venues/${id}/s7000-tunnel`, { method: "DELETE" }),
  // Global Cloudflare API config (admin settings)
  getCloudflareConfig:    () => api("/api/admin/cloudflare-config"),
  updateCloudflareConfig: (data) => api("/api/admin/cloudflare-config", { method: "PUT", body: JSON.stringify(data) }),
  testCloudflareConfig:   (data) => api("/api/admin/cloudflare-config/test", { method: "POST", body: JSON.stringify(data) }),
  testLitecard: (id) => api(`/api/admin/venues/${id}/litecard/test`, { method: "POST" }),
  getLitecardTemplate: (id) => api(`/api/admin/venues/${id}/litecard/template`),
  listLitecardTemplates: (id) => api(`/api/admin/venues/${id}/litecard/templates`),
  selectLitecardTemplate: (id, templateId) =>
    api(`/api/admin/venues/${id}/litecard/template/select`, {
      method: "POST",
      body: JSON.stringify({ templateId }),
    }),
  saveFieldMapping: (id, fieldMapping) =>
    api(`/api/admin/venues/${id}/litecard/field-mapping`, {
      method: "PUT",
      body: JSON.stringify({ field_mapping: fieldMapping }),
    }),
  previewLitecardCard: (id) => api(`/api/admin/venues/${id}/litecard/preview`, { method: "POST" }),
  getVenuePasses: (id, params = {}) => api(`/api/admin/venues/${id}/passes?${new URLSearchParams(params)}`),
  syncVenuePasses: (id) => api(`/api/admin/venues/${id}/passes/sync`, { method: "POST" }),
  getMemberPasses: (venueId, memberId) => api(`/api/admin/venues/${venueId}/members/${memberId}/passes`),
  getMemberPassPreview: (venueId, memberId) => api(`/api/admin/venues/${venueId}/members/${memberId}/pass-preview`),
  getMemberPortalDetails: (venueId, memberId) => api(`/api/admin/venues/${venueId}/members/${memberId}/portal-details`),
  getMemberIdImage: (venueId, memberId, which) => api(`/api/admin/venues/${venueId}/members/${memberId}/id-document/${which}`),
  getMemberLogs: (venueId, memberId) => api(`/api/admin/venues/${venueId}/members/${memberId}/logs`),
  issueMemberCard: (venueId, memberId, mode = "issue") =>
    api(`/api/admin/venues/${venueId}/members/${memberId}/issue-card`, {
      method: "POST",
      body: JSON.stringify({ mode }),
    }),
  updatePassStatus: (venueId, passId, status) => api(`/api/admin/venues/${venueId}/passes/${passId}/status`, { method: "POST", body: JSON.stringify({ status }) }),
  // Email templates (signup-flow welcome + renewal)
  getEmailTemplates: (venueId) => api(`/api/admin/venues/${venueId}/email-templates`),
  saveEmailTemplate: (venueId, key, { subject, body_html, body_blocks }) =>
    api(`/api/admin/venues/${venueId}/email-templates/${key}`, { method: "PUT", body: JSON.stringify({ subject, body_html, body_blocks }) }),
  previewEmailTemplate: (venueId, key, { subject, body_html }) =>
    api(`/api/admin/venues/${venueId}/email-templates/${key}/preview`, { method: "POST", body: JSON.stringify({ subject, body_html }) }),
  sendTestEmail: (venueId, key, { to_email, subject, body_html }) =>
    api(`/api/admin/venues/${venueId}/email-templates/${key}/send-test`, { method: "POST", body: JSON.stringify({ to_email, subject, body_html }) }),
  getEmailSenderStatus: (venueId) => api(`/api/admin/venues/${venueId}/email-sender-status`),
  // Per-venue email provider config
  getEmailProvider: (venueId) => api(`/api/admin/venues/${venueId}/email-provider`),
  saveEmailProvider: (venueId, patch) =>
    api(`/api/admin/venues/${venueId}/email-provider`, { method: "PUT", body: JSON.stringify(patch) }),
  verifyEmailProvider: (venueId, overrides) =>
    api(`/api/admin/venues/${venueId}/email-provider/verify`, { method: "POST", body: JSON.stringify(overrides || {}) }),
  // Analysis Codes
  fetchAnalysisCodes: (venueId) => api(`/api/admin/venues/${venueId}/analysis-codes/fetch`, { method: "POST" }),
  getAnalysisTypes: (venueId) => api(`/api/admin/venues/${venueId}/analysis-codes/types`),
  getAnalysisRules: (venueId) => api(`/api/admin/venues/${venueId}/analysis-codes/rules`),
  saveAnalysisRules: (venueId, rules) => api(`/api/admin/venues/${venueId}/analysis-codes/rules`, { method: "PUT", body: JSON.stringify({ rules }) }),

  // Per-venue API keys (Public Venue API v1)
  listApiKeys: (venueId) => api(`/api/admin/venues/${venueId}/api-keys`),
  createApiKey: (venueId, body) =>
    api(`/api/admin/venues/${venueId}/api-keys`, { method: "POST", body: JSON.stringify(body) }),
  revokeApiKey: (venueId, keyId) =>
    api(`/api/admin/venues/${venueId}/api-keys/${keyId}`, { method: "DELETE" }),

  // Per-venue API request logs (admin-only viewer)
  listApiLogs: (venueId, params = {}) =>
    api(`/api/admin/venues/${venueId}/api-logs?${new URLSearchParams(params)}`),
  getApiLog: (venueId, logId) =>
    api(`/api/admin/venues/${venueId}/api-logs/${logId}`),
  getApiDocs: (venueId, slug) =>
    api(`/api/admin/venues/${venueId}/api-docs?slug=${encodeURIComponent(slug || "")}`),

  // Integration tests v2 — venue-scoped, sandbox-locked
  itPreflight: (venueId) => api(`/api/admin/venues/${venueId}/integration-tests-v2`),
  itPrefill:  (venueId, scenarioId) => api(`/api/admin/venues/${venueId}/integration-tests-v2/${scenarioId}/prefill`),
  itRun:      (venueId, scenarioId, input) =>
    api(`/api/admin/venues/${venueId}/integration-tests-v2/${scenarioId}/run`, {
      method: "POST",
      body: JSON.stringify(input || {}),
    }),
  itPurgeSummary: (venueId) => api(`/api/admin/venues/${venueId}/integration-tests-v2/purge/summary`),
  itPurge:        (venueId) => api(`/api/admin/venues/${venueId}/integration-tests-v2/purge`, { method: "POST" }),

  // Integration tests (legacy)
  listIntegrationTests: () => api("/api/admin/integration-tests"),
  previewIntegrationTest: (venueId, testId, input) =>
    api("/api/admin/integration-tests/preview", { method: "POST", body: JSON.stringify({ venueId, testId, input }) }),
  runIntegrationTest: (venueId, testId, input) =>
    api("/api/admin/integration-tests/run", { method: "POST", body: JSON.stringify({ venueId, testId, input }) }),
};

export const clientApi = {
  // Self
  getMe: () => api("/api/client/me"),
  updateMe: (data) => api("/api/client/me", { method: "PUT", body: JSON.stringify(data) }),
  changeSelfPassword: (currentPassword, newPassword) =>
    api("/api/client/change-password", { method: "PUT", body: JSON.stringify({ currentPassword, newPassword }) }),

  // Dashboard
  getDashboardSummary: () => api("/api/client/dashboard-summary"),

  // Members list — from S7000 cache (venue-wide), with local portal
  // data merged where present. `scope=portal` narrows to only
  // members the portal has a local record for.
  getMembers: (params = {}) => api(`/api/client/members?${new URLSearchParams(params)}`),

  // Unified search + per-member S7000 tools
  searchMembers: (q) => api(`/api/client/members/search?q=${encodeURIComponent(q || "")}`),
  getMemberByCard: (cardNumber) => api(`/api/client/members/by-card/${encodeURIComponent(cardNumber)}`),
  verifyCardholderPin: (data) => api("/api/client/members/by-card/verify", { method: "POST", body: JSON.stringify(data) }),
  getMemberBundle: (membershipId) => api(`/api/client/members/${encodeURIComponent(membershipId)}`),
  getMemberImage: (membershipId) => api(`/api/client/members/${encodeURIComponent(membershipId)}/image`),
  getMemberIdDocument: (membershipId) => api(`/api/client/members/${encodeURIComponent(membershipId)}/id-document`),
  getMemberIdImage: (membershipId, which) => api(`/api/client/members/${encodeURIComponent(membershipId)}/id-document/${which}`),
  getMemberActivity: (membershipId, params = {}) =>
    api(`/api/client/members/${encodeURIComponent(membershipId)}/activity?${new URLSearchParams(params)}`),
  getMemberPlaySessions: (membershipId, params = {}) =>
    api(`/api/client/members/${encodeURIComponent(membershipId)}/play-sessions?${new URLSearchParams(params)}`),
  getMemberEngagement: (membershipId) =>
    api(`/api/client/members/${encodeURIComponent(membershipId)}/engagement`),
  updateMember: (membershipId, data) =>
    api(`/api/client/members/${encodeURIComponent(membershipId)}`, { method: "PUT", body: JSON.stringify(data) }),
  resetMemberPin: (membershipId, data) =>
    api(`/api/client/members/${encodeURIComponent(membershipId)}/reset-pin`, { method: "POST", body: JSON.stringify(data) }),
  suspendMember: (membershipId, data = {}) =>
    api(`/api/client/members/${encodeURIComponent(membershipId)}/suspend`, { method: "POST", body: JSON.stringify(data) }),
  unsuspendMember: (membershipId) =>
    api(`/api/client/members/${encodeURIComponent(membershipId)}/unsuspend`, { method: "POST", body: JSON.stringify({}) }),
  replaceMemberCard: (membershipId, data) =>
    api(`/api/client/members/${encodeURIComponent(membershipId)}/replace-card`, { method: "POST", body: JSON.stringify(data) }),

  // Passes
  getPasses: (params = {}) => api(`/api/client/passes?${new URLSearchParams(params)}`),
  getMemberPasses: (membershipId) =>
    api(`/api/client/passes/by-member/${encodeURIComponent(membershipId)}`),
  issueMemberPass: (membershipId, mode = "issue") =>
    api(`/api/client/passes/by-member/${encodeURIComponent(membershipId)}/issue`, {
      method: "POST",
      body: JSON.stringify({ mode }),
    }),
  updatePassStatus: (passId, status) =>
    api(`/api/client/passes/${passId}/status`, { method: "POST", body: JSON.stringify({ status }) }),

  // Logs
  getLogs: (params) => api(`/api/client/logs?${new URLSearchParams(params)}`),

  // Reports
  getExpiring: (days = 30) => api(`/api/client/reports/expiring?days=${days}`),
  expiringCsvUrl: (days = 30) => `/api/client/reports/expiring.csv?days=${days}`,
  sendExpiryReminders: (data) =>
    api("/api/client/reports/expiring/send-reminders", { method: "POST", body: JSON.stringify(data || {}) }),
  getAnalysisCoverage: () => api("/api/client/reports/analysis-coverage"),
  getCardsExpiring: (days = 30) => api(`/api/client/reports/cards-expiring?days=${days}`),
  cardsExpiringCsvUrl: (days = 30) => `/api/client/reports/cards-expiring.csv?days=${days}`,
  // Acquisition / campaign analytics (this venue).
  getAcquisition: (params) => api(`/api/client/reports/acquisition${qs(params)}`),
  getAcquisitionMembers: (params) => api(`/api/client/reports/acquisition/members${qs(params)}`),
  signupsCsvUrl: (params) => `/api/client/reports/signups.csv${qs(params)}`,
  getDataQuality: () => api("/api/client/reports/data-quality"),
  getDataQualityMembers: (gap) => api(`/api/client/reports/data-quality/members?gap=${encodeURIComponent(gap)}`),
  getSyncAwareness: (days = 30) => api(`/api/client/reports/sync-awareness?days=${days}`),
  getDemographics: () => api("/api/client/reports/demographics"),
  bulkAwardPoints: (data) =>
    api("/api/client/reports/points/bulk-award", { method: "POST", body: JSON.stringify(data) }),

  // Points (per-member)
  getMemberPoints: (membershipId, pointType = "BONUS") =>
    api(`/api/client/members/${encodeURIComponent(membershipId)}/points?pointType=${encodeURIComponent(pointType)}`),
  awardMemberPoints: (membershipId, data) =>
    api(`/api/client/members/${encodeURIComponent(membershipId)}/points/award`, { method: "POST", body: JSON.stringify(data) }),
  redeemMemberPoints: (membershipId, data) =>
    api(`/api/client/members/${encodeURIComponent(membershipId)}/points/redeem`, { method: "POST", body: JSON.stringify(data) }),

  // Live floor + EGM registry
  getLiveFloor: () => api("/api/client/live/floor"),
  listEgms: () => api("/api/client/live/egms"),
  upsertEgm: (data) => api("/api/client/live/egms", { method: "POST", body: JSON.stringify(data) }),
  bulkUpsertEgms: (egms) => api("/api/client/live/egms/bulk", { method: "POST", body: JSON.stringify({ egms }) }),
  deleteEgm: (id) => api(`/api/client/live/egms/${id}`, { method: "DELETE" }),

  // Cache sync (S7000 member mirror)
  getSyncStatus: () => api("/api/client/settings/sync-status"),
  syncNow: () => api("/api/client/settings/sync-now", { method: "POST", body: JSON.stringify({}) }),

  // Member merge (admin only)
  getDuplicateGroups: () => api("/api/client/merge/duplicates"),
  getDuplicateGroupMembers: (firstname, surname, dob) =>
    api(`/api/client/merge/duplicates/members?firstname=${encodeURIComponent(firstname)}&surname=${encodeURIComponent(surname)}&dob=${encodeURIComponent(dob)}`),
  mergeMembers: (data) => api("/api/client/merge/merge", { method: "POST", body: JSON.stringify(data) }),

  // Products
  getProducts: () => api("/api/client/products"),
  createProduct: (data) => api("/api/client/products", { method: "POST", body: JSON.stringify(data) }),
  updateProduct: (pid, data) => api(`/api/client/products/${pid}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteProduct: (pid) => api(`/api/client/products/${pid}`, { method: "DELETE" }),

  // Settings — venue / branding
  getVenue: () => api("/api/client/settings/venue"),
  updateVenue: (data) => api("/api/client/settings/venue", { method: "PUT", body: JSON.stringify(data) }),

  // Settings — email templates + provider
  getEmailTemplates: () => api("/api/client/settings/email-templates"),
  saveEmailTemplate: (key, { subject, body_html, body_blocks }) =>
    api(`/api/client/settings/email-templates/${key}`, { method: "PUT", body: JSON.stringify({ subject, body_html, body_blocks }) }),
  previewEmailTemplate: (key, { subject, body_html }) =>
    api(`/api/client/settings/email-templates/${key}/preview`, { method: "POST", body: JSON.stringify({ subject, body_html }) }),
  sendTestEmail: (key, { to_email, subject, body_html }) =>
    api(`/api/client/settings/email-templates/${key}/send-test`, { method: "POST", body: JSON.stringify({ to_email, subject, body_html }) }),
  getEmailSenderStatus: () => api("/api/client/settings/email-sender-status"),
  getEmailProvider: () => api("/api/client/settings/email-provider"),
  saveEmailProvider: (patch) =>
    api("/api/client/settings/email-provider", { method: "PUT", body: JSON.stringify(patch) }),
  verifyEmailProvider: (overrides) =>
    api("/api/client/settings/email-provider/verify", { method: "POST", body: JSON.stringify(overrides || {}) }),

  // Settings — analysis codes (S7000 only)
  fetchAnalysisCodes: () => api("/api/client/settings/analysis-codes/fetch", { method: "POST" }),
  getAnalysisTypes:   () => api("/api/client/settings/analysis-codes/types"),
  getAnalysisRules:   () => api("/api/client/settings/analysis-codes/rules"),
  saveAnalysisRules:  (rules) =>
    api("/api/client/settings/analysis-codes/rules", { method: "PUT", body: JSON.stringify({ rules }) }),

  // Settings — litecard
  getLitecard: () => api("/api/client/settings/litecard"),
  updateLitecard: (data) => api("/api/client/settings/litecard", { method: "PUT", body: JSON.stringify(data) }),
  testLitecard: () => api("/api/client/settings/litecard/test", { method: "POST" }),
  listLitecardTemplates: () => api("/api/client/settings/litecard/templates"),
  getLitecardTemplate: () => api("/api/client/settings/litecard/template"),
  selectLitecardTemplate: (templateId) =>
    api("/api/client/settings/litecard/template/select", { method: "POST", body: JSON.stringify({ templateId }) }),
  saveFieldMapping: (fieldMapping) =>
    api("/api/client/settings/litecard/field-mapping", { method: "PUT", body: JSON.stringify({ field_mapping: fieldMapping }) }),

  // Settings — comms preferences (marketing options + reasons)
  getMarketingOptions: () => api("/api/client/settings/marketing-options"),
  createMarketingOption: (data) =>
    api("/api/client/settings/marketing-options", { method: "POST", body: JSON.stringify(data) }),
  updateMarketingOption: (id, data) =>
    api(`/api/client/settings/marketing-options/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteMarketingOption: (id) =>
    api(`/api/client/settings/marketing-options/${id}`, { method: "DELETE" }),
  getMarketingReasons: () => api("/api/client/settings/marketing-reasons"),

  // Staff management (admin only)
  getStaff: () => api("/api/client/settings/staff"),
  createStaff: (data) => api("/api/client/settings/staff", { method: "POST", body: JSON.stringify(data) }),
  updateStaff: (id, data) => api(`/api/client/settings/staff/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  resetStaffPassword: (id, password) =>
    api(`/api/client/settings/staff/${id}/password`, { method: "PUT", body: JSON.stringify({ password }) }),
  deleteStaff: (id) => api(`/api/client/settings/staff/${id}`, { method: "DELETE" }),
};

/**
 * IGT-only client-portal tools. Backend enforces that the caller's
 * venue is IGT; calls from S7000 venues get a 400.
 */
clientApi.igt = {
  changePin: (memberNumber, oldPin, newPin) =>
    api(`/api/client/igt/change-pin`, {
      method: "POST",
      body: JSON.stringify({ memberNumber, oldPin, newPin }),
    }),
  // Universal member lookup — pick one of cardNumber / memberNumber /
  // (firstName, lastName, dob). Backend dispatches to the right MeRS
  // endpoint and attaches restrictions on any hit.
  findMember: (query) =>
    api(`/api/client/igt/find-member`, {
      method: "POST",
      body: JSON.stringify(query),
    }),
  // Confirm a member's identity at the counter — pick one of
  // { cardNumber, pin } or { memberNumber, pin }.
  verifyPin: (query) =>
    api(`/api/client/igt/verify-pin`, {
      method: "POST",
      body: JSON.stringify(query),
    }),
  // Staff-side profile update. Only include the sections you want to
  // change; the rest are left alone server-side.
  updateProfile: (payload) =>
    api(`/api/client/igt/update-profile`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getRefData: () => api(`/api/client/igt/ref-data`),
};

/**
 * adminApi-shaped shim over clientApi so the reusable settings
 * components (EmailsTab, EmailProviderPanel, AnalysisCodesTab,
 * DigitalCardTab) can run in the client portal without a fork.
 * venueId arguments are ignored — the backend already scopes by JWT.
 */
export const clientApiAsAdmin = {
  getEmailTemplates:    (_v) => clientApi.getEmailTemplates(),
  saveEmailTemplate:    (_v, key, data) => clientApi.saveEmailTemplate(key, data),
  previewEmailTemplate: (_v, key, data) => clientApi.previewEmailTemplate(key, data),
  sendTestEmail:        (_v, key, data) => clientApi.sendTestEmail(key, data),
  getEmailSenderStatus: (_v) => clientApi.getEmailSenderStatus(),
  getEmailProvider:     (_v) => clientApi.getEmailProvider(),
  saveEmailProvider:    (_v, patch) => clientApi.saveEmailProvider(patch),
  verifyEmailProvider:  (_v, overrides) => clientApi.verifyEmailProvider(overrides),

  fetchAnalysisCodes:   (_v) => clientApi.fetchAnalysisCodes(),
  getAnalysisTypes:     (_v) => clientApi.getAnalysisTypes(),
  getAnalysisRules:     (_v) => clientApi.getAnalysisRules(),
  saveAnalysisRules:    (_v, rules) => clientApi.saveAnalysisRules(rules),

  // Litecard — note: the client-side updateLitecard PUT replaces the admin's
  // "updateLitecard" path; same endpoint shape (accepts the whole config).
  updateLitecard:           (_v, data) => clientApi.updateLitecard(data),
  testLitecard:             (_v) => clientApi.testLitecard(),
  getLitecardTemplate:      (_v) => clientApi.getLitecardTemplate(),
  listLitecardTemplates:    (_v) => clientApi.listLitecardTemplates(),
  selectLitecardTemplate:   (_v, templateId) => clientApi.selectLitecardTemplate(templateId),
  saveFieldMapping:         (_v, mapping) => clientApi.saveFieldMapping(mapping),

  // Products (used by some components)
  getVenueProducts: (_v) => clientApi.getProducts(),
  updateVenueProduct: (_v, pid, data) => clientApi.updateProduct(pid, data),
  createVenueProduct: (_v, data) => clientApi.createProduct(data),
  deleteVenueProduct: (_v, pid) => clientApi.deleteProduct(pid),

  // Pass management used by DigitalCardTab (phase-2 in client portal, stubbed
  // to existing client endpoints where possible).
  getVenuePasses:      (_v, params) => clientApi.getPasses(params),
  updatePassStatus:    (_v, passId, status) => clientApi.updatePassStatus(passId, status),
  syncVenuePasses:     () => Promise.resolve({ ok: true, skipped: true, reason: "client_portal_does_not_sync" }),
  previewLitecardCard: () => Promise.reject(new Error("Preview not available in Venue Portal")),

  // Uploads — shared endpoint, forward to adminApi uploader since both
  // tokens hit the same /api/upload.
  uploadImage: (...args) => adminApi.uploadImage(...args),
};

export const publicApi = {
  getVenueInfo: (slug) => api(`/api/public/${slug}/info`),
  getProducts: (slug) => api(`/api/public/${slug}/products`),
  lookupMember: (slug, memberNumber) =>
    api(`/api/public/${slug}/lookup-member`, { method: "POST", body: JSON.stringify({ memberNumber }) }),
  // IGT-only: look up a member by name + DOB when they've lost their
  // card/number. Used by the signup flow to deduplicate against an
  // existing membership before letting the user start a "new join".
  lookupMemberByName: (slug, { firstName, lastName, dob }) =>
    api(`/api/public/${slug}/lookup-member-by-name`, { method: "POST", body: JSON.stringify({ firstName, lastName, dob }) }),
  // New-join duplicate guard — name+DOB check before payment. Returns
  // { duplicate, message?, code? }. Only meaningful when the venue has
  // duplicate_check_enabled; the backend fails open otherwise.
  checkDuplicate: (slug, { firstName, lastName, dob }) =>
    api(`/api/public/${slug}/check-duplicate`, { method: "POST", body: JSON.stringify({ firstName, lastName, dob }) }),
  // IGT-only: list of mandatory-field names for this venue, as returned
  // by MeRS /sites/mandatory_fields. Empty for non-IGT. Used by the
  // signup form to mark the right fields as required per-venue.
  getMandatoryFields: (slug) => api(`/api/public/${slug}/mandatory-fields`),
  // Active comms-channel options for this venue (Email, SMS, Mail, etc.
  // for IGT; venue-defined for S7000). Empty list means no checkboxes
  // are shown — combined with `!venue.agm_enabled` the signup form
  // skips the comms step entirely.
  getMarketingOptions: (slug) => api(`/api/public/${slug}/marketing-options`),
  verifyPin: (slug, memberNumber, pin, cardNumber) =>
    api(`/api/public/${slug}/verify-pin`, { method: "POST", body: JSON.stringify({ memberNumber, pin, cardNumber: cardNumber || "" }) }),
  createPaymentIntent: (slug, data) =>
    api(`/api/public/${slug}/create-payment-intent`, { method: "POST", body: JSON.stringify(data) }),
  registerMember: (slug, data) =>
    api(`/api/public/${slug}/register-member`, { method: "POST", body: JSON.stringify(data) }),
  // FOXID licence scan — multipart upload of the front (+ optional back)
  // licence image. Returns { scanRef, fields, documentType } where `fields`
  // pre-fills the Details form. No auth (public signup).
  foxidScan: async (slug, { front, back, source }) => {
    const form = new FormData();
    // Filenames matter: canvas captures are nameless Blobs otherwise.
    form.append("front", front, "front.jpg");
    if (back) form.append("back", back, "back.jpg");
    if (source) form.append("source", source);
    // Client-side timeout so a half-open connection can't hang the spinner
    // forever (backend has its own 45s cap; give it a little slack over that).
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 60000);
    let res;
    try {
      res = await fetch(`/api/public/${slug}/foxid/scan`, { method: "POST", body: form, signal: ctrl.signal });
    } catch (err) {
      if (err && err.name === "AbortError") throw new Error("Licence scan timed out. Please try again or enter your details manually.");
      throw new Error("Licence scan failed. Please try again or enter your details manually.");
    } finally {
      clearTimeout(timer);
    }
    let data = {};
    try { data = await res.json(); } catch { /* non-JSON */ }
    if (!res.ok) throw new Error(data.error || data.message || "Licence scan failed");
    return data;
  },
  // FOXID face-match selfie step (its own step, after the licence). Sends the
  // selfie (or a skip flag) plus the licence scanRef. Returns { scanRef, status }
  // where status ∈ matched | failed | not_performed | skipped.
  foxidSelfie: async (slug, { selfie, scanRef, skip, source }) => {
    const form = new FormData();
    if (selfie) form.append("selfie", selfie, "selfie.jpg");
    if (scanRef) form.append("scanRef", scanRef);
    if (skip) form.append("skip", "true");
    if (source) form.append("source", source);
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 60000);
    let res;
    try {
      res = await fetch(`/api/public/${slug}/foxid/selfie`, { method: "POST", body: form, signal: ctrl.signal });
    } catch (err) {
      if (err && err.name === "AbortError") throw new Error("Face match timed out. Please try again, or skip.");
      throw new Error("Face match failed. Please try again, or skip.");
    } finally {
      clearTimeout(timer);
    }
    let data = {};
    try { data = await res.json(); } catch { /* non-JSON */ }
    if (!res.ok) throw new Error(data.error || data.message || "Face match failed");
    return data;
  },
  // Sandbox-only: confirm the payment server-side with a Stripe test card, so a
  // tester never types card details. Returns { paymentIntentId, status }.
  sandboxConfirmPayment: async (slug, paymentIntentId) => {
    const res = await fetch(`/api/public/${slug}/sandbox/confirm-payment`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ paymentIntentId }),
    });
    let data = {};
    try { data = await res.json(); } catch { /* non-JSON */ }
    if (!res.ok) throw new Error(data.error || data.message || "Test payment failed");
    return data;
  },
  trackTiming: (slug, data) =>
    fetch(`/api/public/${slug}/track-timing`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }).catch(() => {}),
};
